import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { SITE_ORIGIN } from "../../../utils/urls";
import { sanitizeHtml, safeHref } from "../../../utils/sanitizeHtml";
import Icon from "@mdi/react";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiTagOutline,
  mdiFormatListBulleted,
  mdiNewspaperVariantOutline,
  mdiTaxi,
  mdiLinkVariant,
  mdiFacebook,
  mdiTwitter,
  mdiLinkedin,
} from "@mdi/js";
import Navbar from "../../../layout/Navbar";
import DownloadSection from "../../../layout/downloadSection";
import Footer from "../../../layout/Footer";
import SEO from "../../../components/SEO";
import { Helmet } from "react-helmet";

/**
 * BlogPageGem (Dynamic)
 * - Same UI as your current page
 * - Dynamic content via backend JSON (block based)
 * - Auto TOC from headings in content
 * - IntersectionObserver for active TOC
 * - Reading progress
 * - Share + copy link toast
 *
 * Replace fetchBlogBySlug() with your real API call.
 */

function formatInitials(name = "") {
  const parts = String(name).trim().split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "P";
  const b = parts[1]?.[0] || "C";
  return (a + b).toUpperCase();
}

function formatDateLikeUI(dateStr) {
  // If backend already sends "May 15, 2025" you can just return as-is.
  // If it sends ISO "2025-05-15", format it.
  if (!dateStr) return "";
  if (/[A-Za-z]/.test(dateStr)) return dateStr; // already human readable
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function buildShareUrl(platform, url, title) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title || "");
  switch (platform) {
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    default:
      return "#";
  }
}

// blog CONTENT lives in the legacy CMS — see src/utils/cmsBase.js
const API_BASE = process.env.REACT_APP_CMS_URL || "https://customerapi.pulpitcabs.com";

async function fetchBlogFromList(slug) {
  const res = await fetch(`${API_BASE}/website/get_blogs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  const json = await res.json();
  if (json.status !== 200 || !Array.isArray(json.data)) return null;
  return json.data.find((b) => b?.slug === slug) || null;
}

async function fetchBlogBySlug(slug) {
  if (!slug) throw new Error("Blog slug is required");
  const res = await fetch(`${API_BASE}/website/get_cabs_json_details`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
  const json = await res.json();
  // get_cabs_json_details has no record for any of the published posts (it
  // returns data:null for all 12), so the real content lives in get_blogs'
  // own description field. Fall back to it rather than rendering a soft 404.
  const raw = json.status === 200 && json.data ? json.data : await fetchBlogFromList(slug);
  if (!raw) throw new Error("Failed to fetch blog");
  let payload = raw.description;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      // Plain HTML from get_blogs, not the JSON block format - wrap it in a
      // single html content block.
      payload = {
        title: raw.title,
        content: [{ type: "html", html: raw.description }],
        meta: { metaDescription: raw.meta_description, metaKeywords: raw.meta_keywords },
        cover: Array.isArray(raw.images) && raw.images.length ? { imageUrl: raw.images[0], alt: raw.title } : null,
        tags: Array.isArray(raw.tags) ? raw.tags : [],
      };
    }
  }
  const coverRaw = payload.cover;
  const cover = Array.isArray(coverRaw) && coverRaw.length > 0
    ? coverRaw[0]
    : coverRaw && typeof coverRaw === "object"
      ? coverRaw
      : null;
  let tags = payload.tags;
  if (Array.isArray(tags) && tags.length > 0 && Array.isArray(tags[0])) {
    tags = tags.flat();
  }
  if (!Array.isArray(tags)) tags = [];
  return {
    id: payload.id || raw.id,
    slug: payload.slug || raw.slug || slug,
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
    metaAuthor: raw.meta_author || null,
    breadcrumbs: payload.breadcrumbs || [
      { label: "Home", href: "/" },
      { label: "Blogs", href: "/blogs" },
      { label: payload.title || "Blog", href: null },
    ],
    category: payload.category || { label: "Blog", slug: "blog" },
    title: payload.title || "Untitled",
    meta: payload.meta || {},
    author: payload.author || { name: "Pulpit Cabs", initials: "PC", bio: "", avatarUrl: null },
    cover: cover ? { imageUrl: cover.imageUrl, alt: cover.alt || payload.title } : null,
    share: payload.share || { enabled: true, platforms: ["facebook", "twitter", "linkedin"], copyLink: true },
    tags,
    toc: payload.toc || { enabled: true },
    content: Array.isArray(payload.content) ? payload.content : [],
    related: payload.related || null,
    cta: payload.cta || { enabled: true, title: "Book a Ride Now", description: "Safe, reliable cab service.", buttonText: "Book Now →", href: "/", icon: "taxi" },
  };
}

function ContentRenderer({ blocks }) {
  return (
    <>
      {blocks.map((b, idx) => {
        if (b.type === "html") {
          // CMS-authored HTML from get_blogs; same trust level as the JSON
          // blocks, both come from the team's own CMS.
          return <div key={idx} dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.html) }} />;
        }
        if (b.type === "paragraph") {
          return <p key={idx}>{b.text}</p>;
        }

        if (b.type === "heading") {
          const H = b.level === 3 ? "h3" : "h2";
          return (
            <H key={b.id || idx} id={b.id} className={b.level === 3 ? "text-[18px] mt-8 mb-3" : ""}>
              {b.text}
            </H>
          );
        }

        if (b.type === "quote") {
          return (
            <blockquote
              key={idx}
              className="not-prose my-8 rounded-r-[14px] border-l-4 border-[#E05A49] bg-[#E05A49]/10 px-6 py-5"
            >
              <p className="text-[18px] italic text-[#0B1F3A] font-semibold mb-2">"{b.quote}"</p>
              {b.cite ? <cite className="text-[13px] text-slate-400 not-italic">{b.cite}</cite> : null}
            </blockquote>
          );
        }

        if (b.type === "bulletList") {
          return (
            <div key={idx} className="not-prose mb-6">
              <ul className="space-y-2.5">
                {(b.items || []).map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[17px] leading-[1.7] text-slate-700">
                    <span className="w-2 h-2 rounded-full bg-[#E05A49] mt-[9px] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        if (b.type === "image") {
          return (
            <figure key={idx} className="not-prose my-7">
              <img
                src={b.imageUrl}
                alt={b.alt || "Blog image"}
                className="w-full rounded-[14px] shadow-[0_2px_16px_rgba(0,0,0,0.08)] max-h-[380px] object-cover"
              />
              {b.caption ? (
                <figcaption className="text-center text-[13px] text-slate-400 mt-2">{b.caption}</figcaption>
              ) : null}
            </figure>
          );
        }

        if (b.type === "proTip") {
          return (
            <div
              key={idx}
              className="not-prose my-7 rounded-[14px] border border-[#0B1F3A]/15 bg-[#0B1F3A]/[0.03] px-6 py-5"
            >
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#E05A49] font-extrabold mb-1">
                {b.label || "⚡ Pro Tip"}
              </div>
              <p className="text-[15px] text-slate-700">{b.text}</p>
            </div>
          );
        }

        // Unknown block -> ignore (safe)
        return null;
      })}
    </>
  );
}

export default function BlogPageGemDynamic(props) {
  const { slug: slugFromRoute } = useParams();
  const slug = props.slug ?? slugFromRoute ?? "";

  const [progress, setProgress] = useState(0);
  const [tocOpen, setTocOpen] = useState(true);
  const [activeToc, setActiveToc] = useState("");
  const [toast, setToast] = useState(false);

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const observerRef = useRef(null);
  const tocClickTimeRef = useRef(0);

  // ====== Fetch blog ======
  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setErr("Blog slug is required");
      return;
    }
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const data = await fetchBlogBySlug(slug);
        if (!mounted) return;
        setBlog(data);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.message || "Something went wrong");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  // ====== Derived TOC from content headings ======
  const tocItems = useMemo(() => {
    const blocks = blog?.content || [];
    const items = blocks
      .filter((b) => b?.type === "heading" && b?.includeInToc && b?.id)
      .map((b) => ({ id: b.id, label: b.text }));
    return items;
  }, [blog]);

  // set initial active toc
  useEffect(() => {
    if (tocItems.length) setActiveToc((prev) => prev || tocItems[0].id);
  }, [tocItems]);

  // ====== Reading Progress ======
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop || document.body.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      const pct = total > 0 ? (scrolled / total) * 100 : 0;
      setProgress(Math.min(100, pct));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ====== TOC Intersection Observer ======
  useEffect(() => {
    if (!tocItems.length) return;

    if (observerRef.current) observerRef.current.disconnect();

    const tocObserver = new IntersectionObserver(
      (entries) => {
        const now = Date.now();
        if (now - tocClickTimeRef.current < 800) return; // keep clicked item selected briefly
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target?.id) {
            setActiveToc(entry.target.id);
          }
        });
      },
      { rootMargin: "-80px 0px -60% 0px" }
    );

    tocItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) tocObserver.observe(el);
    });

    observerRef.current = tocObserver;
    return () => tocObserver.disconnect();
  }, [tocItems]);

  const containerX = "px-4 sm:px-6 lg:px-10 2xl:px-14";
  const container = `max-w-screen-2xl mx-auto ${containerX}`;

  function copyLink() {
    const url = window.location.href;
    navigator.clipboard?.writeText(url).then(() => {
      setToast(true);
      setTimeout(() => setToast(false), 2500);
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        <SEO title="Blog Post" description="Read the latest updates and travel tips from Pulpit cab service" />
        <Navbar />
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 2xl:px-14 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-6 w-40 bg-slate-200 rounded" />
            <div className="h-10 w-[70%] bg-slate-200 rounded" />
            <div className="h-4 w-[40%] bg-slate-200 rounded" />
            <div className="h-[360px] w-full bg-slate-200 rounded-[20px]" />
            <div className="h-4 w-[85%] bg-slate-200 rounded" />
            <div className="h-4 w-[78%] bg-slate-200 rounded" />
            <div className="h-4 w-[66%] bg-slate-200 rounded" />
          </div>
        </div>
        <DownloadSection />
        <Footer />
      </div>
    );
  }

  if (err || !blog) {
    return (
      <div className="min-h-screen bg-white text-slate-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        <SEO title="Blog Not Found" description="The blog post you're looking for doesn't exist or has been removed." />
        <Navbar />
        <div className="max-w-screen-2xl  mx-auto px-4 sm:px-6 lg:px-10 2xl:px-14 ">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 mb-96">
            <div className="text-[#0B1F3A] font-black text-xl mb-2">Unable to load blog</div>
            <div className="text-slate-600 text-sm">{err || "Blog not found"}</div>
          </div>
        </div>
        <DownloadSection />
        <Footer />
      </div>
    );
  }

  // Canonical blog URL, not window.location.href: the prerender step renders on
  // http://127.0.0.1:4321, which was being baked into every share button so a
  // real click shared a dead localhost link.
  const shareUrl = `${SITE_ORIGIN}/blog/${blog.slug || slugFromRoute || ""}`;
  const shareTitle = blog.title;

  const showSidebar = true; // sidebar is hidden on <lg via Tailwind anyway

  return (
    <div className="min-h-screen bg-white text-slate-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <SEO
        title={blog?.title || "Blog Post"}
        description={blog?.meta?.metaDescription || blog?.meta_description || "Read the latest updates and travel tips from Pulpit cab service"}
        keywords={blog?.meta?.metaKeywords || blog?.meta_keywords}
      />
      {/* Blog posts shipped with no Article markup at all - no headline, no
          dates, no author for a rich result to use. Dates come from the CMS
          record, never invented. */}
      <Helmet defer={false}>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: blog.title,
            ...(blog.cover?.imageUrl ? { image: [blog.cover.imageUrl] } : {}),
            ...(blog.createdAt ? { datePublished: blog.createdAt } : {}),
            ...(blog.updatedAt ? { dateModified: blog.updatedAt } : {}),
            author: {
              "@type": blog.metaAuthor || blog.author?.name ? "Person" : "Organization",
              name: blog.metaAuthor || blog.author?.name || "Pulpit Cabs",
            },
            publisher: {
              "@type": "Organization",
              name: "Pulpit Cabs",
              logo: { "@type": "ImageObject", url: "https://pulpitcabs.com/logo512.png" },
            },
            mainEntityOfPage: `https://pulpitcabs.com/blog/${blog.slug}/`,
            ...(blog.tags?.length ? { keywords: blog.tags.join(", ") } : {}),
          })}
        </script>
      </Helmet>
      <Navbar />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Montserrat:wght@400;500;600;700;800&display=swap');
        .blog-page { font-family: 'Montserrat', sans-serif; }
        .blog-page h1, .blog-page h2, .blog-page h3, .blog-page h4 { font-family: 'Inter', sans-serif; }
        .reading-fill {
          background: linear-gradient(90deg, #E05A49, #c94a3f);
          transition: width 0.1s;
        }
        .toast {
          transform: translateX(-50%) translateY(16px);
          opacity: 0;
          transition: all 0.3s;
        }
        .toast.show {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
        }
      `}</style>

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-slate-200 z-[999]">
        <div className="h-full reading-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Breadcrumb */}
      <div className="bg-[#21A5A0] border-b border-[#21A5A0]">
        <div className={`${container} py-2.5`}>
          <nav className="flex flex-wrap items-center gap-1.5 text-[13px] text-white/90" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {(blog.breadcrumbs || []).map((b, idx) => {
              const isLast = idx === (blog.breadcrumbs || []).length - 1;
              return (
                <React.Fragment key={`${b.label}-${idx}`}>
                  {idx > 0 ? <span className="text-[10px] text-white/70">›</span> : null}
                  {b.href && !isLast ? (
                    <a href={b.href} className="hover:text-white transition">
                      {b.label}
                    </a>
                  ) : (
                    <span className="text-white font-semibold">{b.label}</span>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className={`${container} pt-9`}>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#E05A49]/60 bg-[#E05A49]/10 text-[#E05A49] font-extrabold text-[11px] uppercase tracking-[0.12em] px-4 py-1.5 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
          <Icon path={mdiTagOutline} size={0.75} />
          {blog.category?.label || "Category"}
        </div>

        <h1 className="text-[#0B1F3A] font-black leading-tight text-[clamp(26px,4vw,44px)] max-w-[820px] mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
          {blog.title}
        </h1>

        <div className="flex flex-wrap items-center gap-5 text-[13px] text-slate-500 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#0B1F3A] text-white flex items-center justify-center text-[12px] font-extrabold" style={{ fontFamily: "'Inter', sans-serif" }}>
              {blog.author?.avatarUrl ? (
                <img
                  src={blog.author.avatarUrl}
                  alt={blog.author?.name || "Author"}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                formatInitials(blog.author?.name || "")
              )}
            </div>
            <strong className="text-[#0B1F3A]" style={{ fontFamily: "'Inter', sans-serif" }}>{blog.author?.name || "Author"}</strong>
          </div>

          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E05A49]" />
            {formatDateLikeUI(blog.meta?.publishedAt)}
          </div>

          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E05A49]" />
            {blog.meta?.readTimeMin ? `${blog.meta.readTimeMin} min read` : ""}
          </div>
        </div>

        {/* Share strip */}
        {blog.share?.enabled ? (
          <div className="flex items-center gap-2.5 mb-7">
            <span className="text-[11px] uppercase tracking-[0.14em] text-slate-400 font-extrabold" style={{ fontFamily: "'Inter', sans-serif" }}>
              Share:
            </span>

            {(blog.share?.platforms || []).includes("facebook") ? (
              <a
                href={buildShareUrl("facebook", shareUrl, shareTitle)}
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:border-[#E05A49] hover:bg-[#E05A49]/10 transition"
                title="Facebook"
              >
                <Icon path={mdiFacebook} size={0.7} className="text-[#1877F2]" />
              </a>
            ) : null}

            {(blog.share?.platforms || []).includes("twitter") ? (
              <a
                href={buildShareUrl("twitter", shareUrl, shareTitle)}
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:border-[#E05A49] hover:bg-[#E05A49]/10 transition"
                title="Twitter"
              >
                <Icon path={mdiTwitter} size={0.7} className="text-[#1DA1F2]" />
              </a>
            ) : null}

            {(blog.share?.platforms || []).includes("linkedin") ? (
              <a
                href={buildShareUrl("linkedin", shareUrl, shareTitle)}
                target="_blank"
                rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:border-[#E05A49] hover:bg-[#E05A49]/10 transition"
                title="LinkedIn"
              >
                <Icon path={mdiLinkedin} size={0.7} className="text-[#0A66C2]" />
              </a>
            ) : null}

            {blog.share?.copyLink ? (
              <button
                type="button"
                onClick={copyLink}
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:border-[#E05A49] hover:bg-[#E05A49]/10 transition"
                title="Copy link"
              >
                <Icon path={mdiLinkVariant} size={0.78} className="text-slate-700" />
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* Cover Image */}
      {blog.cover?.imageUrl ? (
        <section className={`${container} pb-2`}>
          <div className="rounded-[20px] overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.13)] max-h-[500px] relative">
            <img
              src={blog.cover.imageUrl}
              alt={blog.cover?.alt || blog.title}
              className="w-full max-h-[500px] object-cover block"
            />
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/25 to-transparent" />
          </div>
        </section>
      ) : null}

      {/* Main Grid */}
      <section className={`${container} pt-10 pb-20`}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10 lg:gap-12">
          {/* Article */}
          <main>
            <article
              className="
                prose prose-slate max-w-none blog-page
                prose-p:text-[14px]
                prose-p:leading-[1.8]
                prose-p:text-slate-700
                prose-p:font-medium
                prose-headings:text-[#0B1F3A]
                prose-headings:font-extrabold
                prose-headings:font-['Inter']
                prose-h2:text-[18px]
                prose-h2:mt-8
                prose-h2:mb-3
                prose-strong:text-[#0B1F3A]
              "
            >
              <ContentRenderer blocks={blog.content || []} />
            </article>

            {/* Author Card */}
            <div className="mt-11 rounded-[18px] border border-slate-200 bg-slate-50 p-6 flex gap-5 items-start">
              <div className="w-16 h-16 rounded-full bg-[#0B1F3A] text-[#E05A49] flex items-center justify-center text-3xl font-black shrink-0 overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
                {blog.author?.avatarUrl ? (
                  <img
                    src={blog.author.avatarUrl}
                    alt={blog.author?.name || "Author"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  (blog.author?.name || "R")[0]?.toUpperCase()
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#E05A49] font-extrabold mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Written by
                </div>
                <h4 className="text-[17px] font-extrabold text-[#0B1F3A] mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>{blog.author?.name}</h4>
                <p className="text-[14px] text-slate-500 leading-[1.65]">{blog.author?.bio}</p>
              </div>
            </div>

            {/* Tags */}
            {(blog.tags || []).length ? (
              <div className="mt-7 flex flex-wrap gap-2">
                {blog.tags.map((t) => (
                  <button
                    key={t.slug || t.label}
                    className="text-[12px] px-3.5 py-1.5 rounded-full border border-slate-200 text-slate-500 hover:border-[#E05A49] hover:text-[#E05A49] hover:bg-[#E05A49]/10 transition"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            ) : null}

            {/* Related Posts */}
            {(blog.related?.cards || []).length ? (
              <div className="mt-14 pt-11 border-t border-slate-200">
                <div className="flex items-end justify-between gap-4 mb-7">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#E05A49] font-extrabold mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Keep Reading
                    </div>
                    <h2 className="text-[#0B1F3A] font-black text-[clamp(20px,2.5vw,28px)]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {blog.related?.heading || "Related Articles"}
                    </h2>
                  </div>
                  {blog.related?.viewAllHref ? (
                    <a href={safeHref(blog.related.viewAllHref)} className="text-[13px] text-[#E05A49] font-bold hover:underline" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      View all →
                    </a>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {blog.related.cards.map((r) => (
                    <a
                      key={r.id || r.slug || r.title}
                      href={r.slug ? `/blog/${r.slug}` : "#"}
                      className="rounded-2xl overflow-hidden border border-slate-100 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_28px_rgba(0,0,0,0.10)] hover:-translate-y-0.5 transition flex flex-col"
                    >
                      <div className="h-44 overflow-hidden relative">
                        <img
                          src={r.coverImageUrl}
                          alt={r.title}
                          className="w-full h-full object-cover hover:scale-[1.05] transition"
                        />
                        {r.chip ? (
                          <span className="absolute top-2.5 left-2.5 text-[10px] font-bold text-[#0B1F3A] bg-white/90 backdrop-blur px-2.5 py-1 rounded-full" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {r.chip}
                          </span>
                        ) : null}
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <h3 className="text-[14px] font-extrabold text-[#0B1F3A] leading-snug line-clamp-2 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {r.title}
                        </h3>
                        <p className="text-[13px] text-slate-500 leading-relaxed line-clamp-2 mb-3 flex-1">{r.excerpt || "Read more..."}</p>
                        <div className="flex justify-between text-[11px] text-slate-400 border-t border-slate-100 pt-2.5">
                          <span>{formatDateLikeUI(r.publishedAt)}</span>
                          <span>{r.readTimeMin ? `${r.readTimeMin} min read` : ""}</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </main>

          {/* Sidebar */}
          {showSidebar ? (
            <aside className="hidden lg:block">
              <div className="sticky top-5 space-y-5">
                {/* TOC */}
                {blog.toc?.enabled && tocItems.length ? (
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setTocOpen((v) => !v)}
                      className="w-full bg-[#21A5A0] px-4 py-3.5 flex items-center justify-between"
                    >
                      <span className="text-white font-bold text-[13px] inline-flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                        <Icon path={mdiFormatListBulleted} size={0.75} />
                        Table of Contents
                      </span>
                      <span className="text-white/70">
                        <Icon path={tocOpen ? mdiChevronUp : mdiChevronDown} size={0.8} />
                      </span>
                    </button>

                    {tocOpen ? (
                      <nav className="p-3">
                        {tocItems.map((h) => (
                          <a
                            key={h.id}
                            href={`#${h.id}`}
                            onClick={() => {
                              tocClickTimeRef.current = Date.now();
                              setActiveToc(h.id);
                            }}
                            className={[
                              "block text-[12px] px-3 py-2 rounded-lg transition border-l-2",
                              activeToc === h.id
                                ? "bg-[#21A5A0]/10 text-[#21A5A0] font-bold border-[#21A5A0]"
                                : "text-slate-500 hover:bg-[#21A5A0]/10 hover:text-[#21A5A0] border-transparent",
                            ].join(" ")}
                            style={{ fontFamily: "'Montserrat', sans-serif" }}
                          >
                            {h.label}
                          </a>
                        ))}
                      </nav>
                    ) : null}
                  </div>
                ) : null}

                {/* Tags */}
                {(blog.tags || []).length ? (
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
                    <div className="px-4 py-3.5 border-b border-slate-100 text-[#0B1F3A] font-extrabold text-[14px] flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <Icon path={mdiTagOutline} size={0.8} />
                      Tags
                    </div>
                    <div className="p-3 flex flex-wrap gap-2">
                      {blog.tags.map((t) => (
                        <a
                          key={t.slug || t.label}
                          href={t.slug ? `/blogs?tag=${encodeURIComponent(t.slug)}` : "#"}
                          className="text-[11px] px-3 py-1.5 rounded-full border border-slate-200 text-slate-500 hover:border-[#E05A49] hover:text-[#E05A49] hover:bg-[#E05A49]/10 transition"
                          style={{ fontFamily: "'Montserrat', sans-serif" }}
                        >
                          {t.label}
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Related mini */}
                {(blog.related?.miniList || []).length ? (
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
                    <div className="px-4 py-3.5 border-b border-slate-100 text-[#0B1F3A] font-extrabold text-[14px] flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <Icon path={mdiNewspaperVariantOutline} size={0.8} />
                      Related Articles
                    </div>
                    <div className="p-3 space-y-4">
                      {blog.related.miniList.map((m) => (
                        <a
                          key={m.id || m.slug || m.title}
                          href={m.slug ? `/blog/${m.slug}` : "#"}
                          className="flex gap-3 items-start group"
                        >
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                            <img
                              src={m.thumbUrl}
                              alt={m.title}
                              className="w-full h-full object-cover group-hover:scale-[1.07] transition"
                            />
                          </div>
                          <div>
                            <h4 className="text-[12px] font-bold text-[#0B1F3A] leading-snug line-clamp-2 group-hover:text-[#E05A49] transition" style={{ fontFamily: "'Inter', sans-serif" }}>
                              {m.title}
                            </h4>
                            <div className="text-[10px] text-slate-400 mt-1">{m.meta}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* CTA */}
                {blog.cta?.enabled ? (
                  <div className="rounded-2xl bg-[#0B1F3A] p-6 text-center">
                    <div className="w-13 h-13 w-[52px] h-[52px] rounded-full bg-[#E05A49]/20 flex items-center justify-center mx-auto mb-3">
                      <Icon path={mdiTaxi} size={1} className="text-[#E05A49]" />
                    </div>
                    <h3 className="text-white font-extrabold text-[16px] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{blog.cta.title}</h3>
                    <p className="text-white/70 text-[13px] leading-relaxed mb-4">{blog.cta.description}</p>
                    <a
                      href={blog.cta.href || "/"}
                      className="inline-flex items-center gap-2 rounded-full bg-[#E05A49] hover:bg-[#c94a3f] transition px-5 py-2.5 text-white font-bold text-[13px]"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {blog.cta.buttonText || "Book Now →"}
                    </a>
                  </div>
                ) : null}
              </div>
            </aside>
          ) : null}
        </div>
      </section>

      {/* Toast */}
      <div
        className={[
          "fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0B1F3A] text-white text-[13px] px-5 py-2.5 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.18)] z-[9999] toast",
          toast ? "show" : "",
        ].join(" ")}
      >
        ✅ Link copied to clipboard!
      </div>
      <DownloadSection />
      <Footer />
    </div>
  );
}